import { encodeWav } from "./wav.js";
import type {
  Segment,
  TranscribeOptions,
  TranscribeResult,
} from "./types.js";

const DEFAULT_BASE_URL = "https://transcribe.unveiledsoftwaresolutions.com";
const DEFAULT_INLINE_WAIT_MS = 60_000;
const DEFAULT_POLL_UNTIL_MS = 10 * 60 * 1000;
const DEFAULT_POLL_INTERVAL_MS = 2_000;

/**
 * Submit audio to the cluster's transcription service.
 *
 * Accepts either:
 * - **`Float32Array`** — mono 16 kHz PCM samples (as produced by `record()`).
 *   The lib wraps these in a WAV header before upload.
 * - **`Blob` / `File`** — any container the cluster decodes (WAV, MP3, FLAC,
 *   OGG, M4A). Uploaded as-is. If you have raw PCM bytes, do **not** wrap
 *   them in a Blob and pass them here — pass a `Float32Array` instead so
 *   the lib produces a proper WAV.
 *
 * The cluster's response is awaited inline up to `inlineWaitMs`; if the
 * job is still running after that, the lib polls `/v1/jobs/:id` until
 * `pollUntilMs` elapses or the job terminates.
 */
export async function transcribe(
  audio: Float32Array | Blob | File,
  opts: TranscribeOptions = {},
): Promise<TranscribeResult> {
  const baseUrl = (opts.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, "");
  const inlineWait = opts.inlineWaitMs ?? DEFAULT_INLINE_WAIT_MS;
  const pollUntil = opts.pollUntilMs ?? DEFAULT_POLL_UNTIL_MS;

  const { blob, filename } = audioToWavOrPassThrough(audio);

  const form = new FormData();
  form.append("audio", blob, filename);
  if (opts.model) form.append("model", opts.model);
  if (opts.language) form.append("language", opts.language);
  if (opts.timestamps !== undefined) {
    form.append("timestamps", String(opts.timestamps));
  }
  if (opts.profile) form.append("profile", opts.profile);
  if (opts.initialPrompt) form.append("initial_prompt", opts.initialPrompt);
  if (opts.batch) form.append("priority", "batch");

  const submitUrl = opts.endpoint
    ? appendQuery(opts.endpoint, "inline_wait_ms", String(inlineWait))
    : `${baseUrl}/v1/transcribe?inline_wait_ms=${inlineWait}`;
  const headers = bearerHeader(opts);

  const submit = await fetch(submitUrl, {
    method: "POST",
    headers,
    body: form,
  });

  if (!submit.ok && submit.status !== 202) {
    const body = await submit.text().catch(() => "");
    throw new TranscribeError(
      submit.status,
      `voice-engine submit failed: HTTP ${submit.status}`,
      body,
    );
  }

  let job = await submit.json();
  if (job.status === "complete" || job.status === "failed") {
    return finalize(job, submit.status);
  }

  // 202 — server returned a job_id, poll for completion.
  const jobId = job.job_id as string | undefined;
  if (!jobId) {
    throw new TranscribeError(
      submit.status,
      "voice-engine: 202 returned without job_id; cannot poll",
    );
  }

  const jobsUrl = opts.endpoint
    ? deriveJobsUrl(opts.endpoint, jobId)
    : `${baseUrl}/v1/jobs/${jobId}`;

  const deadline = Date.now() + pollUntil;
  while (Date.now() < deadline) {
    await delay(DEFAULT_POLL_INTERVAL_MS);
    const r = await fetch(jobsUrl, { headers });
    if (!r.ok) {
      const body = await r.text().catch(() => "");
      throw new TranscribeError(
        r.status,
        `voice-engine poll failed: HTTP ${r.status}`,
        body,
      );
    }
    job = await r.json();
    if (job.status === "complete" || job.status === "failed") {
      return finalize(job, r.status);
    }
  }
  throw new TranscribeError(
    408,
    `voice-engine: transcription not complete within ${pollUntil}ms`,
  );
}

function audioToWavOrPassThrough(
  audio: Float32Array | Blob | File,
): { blob: Blob; filename: string } {
  if (audio instanceof Float32Array) {
    return { blob: encodeWav(audio), filename: "audio.wav" };
  }
  // Blob or File — assume the caller knows the container is one the
  // cluster supports. Always supply a sensible filename so the server
  // doesn't fall through to its `.bin` extension default.
  const filename =
    (audio as File).name && (audio as File).name.length > 0
      ? (audio as File).name
      : guessFilenameFromMime(audio.type);
  return { blob: audio, filename };
}

function guessFilenameFromMime(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes("wav")) return "audio.wav";
  if (m.includes("mpeg") || m.includes("mp3")) return "audio.mp3";
  if (m.includes("flac")) return "audio.flac";
  if (m.includes("ogg")) return "audio.ogg";
  if (m.includes("mp4") || m.includes("m4a") || m.includes("aac")) {
    return "audio.m4a";
  }
  return "audio.wav";
}

function bearerHeader(opts: TranscribeOptions): Record<string, string> {
  // When using a proxy (`endpoint`), the proxy is responsible for adding
  // the cluster's Bearer token server-side. Don't leak the token to a
  // browser-side bundle.
  if (opts.token && !opts.endpoint) {
    return { Authorization: `Bearer ${opts.token}` };
  }
  return {};
}

function appendQuery(url: string, key: string, value: string): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

/** Derive a `/jobs/:id` URL from a custom proxy endpoint.
 *
 * If the proxy is `/api/voice/transcribe`, polling hits `/api/voice/jobs/:id`.
 * If the proxy ends differently, we fall back to appending `/jobs/:id` to
 * its parent path. */
function deriveJobsUrl(endpoint: string, jobId: string): string {
  const url = stripQuery(endpoint);
  if (url.endsWith("/transcribe")) {
    return `${url.substring(0, url.length - "/transcribe".length)}/jobs/${jobId}`;
  }
  // Generic fallback: parent path + /jobs/:id
  const idx = url.lastIndexOf("/");
  const parent = idx > 0 ? url.substring(0, idx) : url;
  return `${parent}/jobs/${jobId}`;
}

function stripQuery(url: string): string {
  const i = url.indexOf("?");
  return i === -1 ? url : url.substring(0, i);
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

interface RawJob {
  status: string;
  job_id?: string;
  text?: string;
  segments?: Segment[];
  error?: string;
  device?: string;
  elapsed_ms?: number;
  model?: string;
}

function finalize(job: RawJob, httpStatus: number): TranscribeResult {
  if (job.status === "failed") {
    throw new TranscribeError(
      httpStatus === 200 ? 422 : httpStatus,
      job.error ?? "transcription failed",
    );
  }
  return {
    text: job.text ?? "",
    segments: job.segments ?? [],
    device: job.device,
    elapsedMs: job.elapsed_ms,
    model: job.model,
  };
}

/** Thrown by `transcribe()` on any non-success response from the cluster. */
export class TranscribeError extends Error {
  public override readonly name = "TranscribeError";
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: string,
  ) {
    super(message);
  }
}
